/* eslint-disable import/no-cycle */
import { StatusType } from '../api.types';
import { UserRole } from '../authentication/authentication.types';
import { MandatoryItemPriceModel } from '../pricing/pricing.types';

/*
* Car Models
*/
export interface CarModelRequest {
  statusType: StatusType;
  acrissCode: string;
  name: string;
  rank?: number;
  tag?: string;
  displayName: string;
  base64Image: string;
  hasPrice: boolean;
  isGuaranteedModel: boolean;
  isOneWayRentalAllowed: boolean;
  minDriverAge: number;
  youngDriverAge: number;
  supplierReferenceCode: string;

  driveType: CarModelDriveType;
  fuelType: CarModelFuelType;
  transmissionType: CarModelTransmissionType;
  baggageCountBig: number;
  baggageCountSmall: number;
  doorCount: number;
  passengerCount: number;
  hasAirConditioning: boolean;
  carModelFeatures: CarModelFeatureModel[];
  alternativeCars: AlternativeCarModel[];

  depositAmount?: string;
  excessAmount?: number;
  excessCurrencyCode?: string;
}

export interface CarModelModel {
  id: number;
  statusType: StatusType;
  acrissCode: string;
  name: string;
  displayName: string;
  image: Image;
  hasPrice: boolean;
  rank: number;
  tag: string;
  isOneWayRentalAllowed: boolean;
  isGuaranteedModel: boolean;
  minDriverAge: number;
  youngDriverAge: number;
  supplierReferenceCode: string;
  associatedRatePlansNo: number;

  driveType: CarModelDriveType;
  fuelType: CarModelFuelType;
  transmissionType: CarModelTransmissionType;
  baggageCountBig: number;
  baggageCountSmall: number;
  doorCount: number;
  passengerCount: number;
  hasAirConditioning: boolean;
  carModelFeatures: CarModelFeatureModel[];
  alternativeCars: AlternativeCarModel[];

  depositAmount: string;
  excessAmount: number;
  excessCurrencyCode: string;

  modifiedAt: string;
  modifiedBy: string;

  createdAt: string;
  createdBy: string;
}

export interface CarClassModel {
  acrissCode: string;
  id: number;
  name: string;
}

export interface Image {

  tiny: string;
  small: string;
  medium: string;
  large: string;
  original: string;
}

export interface CarModelRankItem {
  id: number;
  value?: number;
  initialValue?: number;
  rowItem: CarModelRequest;
}

export interface CarModelFeatureModel {
  featureCode: string;
}

export interface AlternativeCarModel {
  id: number;
}

export interface CarModelListFilters {
  pageNumber?: number;
  combinedFieldFilter?: string;
  tag?: string;
  hasPrice?: string;
}

interface TransmissionDriveVal {
  transmission: CarModelTransmissionType;
  drive: CarModelDriveType;
}

export interface TransmissionDriveMap {
  [ key: string ]: TransmissionDriveVal;
}

export enum AirCoVal {
  WITH_AC = 'With AC',
  NO_AC = 'No AC'
}

interface FuelAcVal {
  fuel: CarModelFuelType;
  airCo: AirCoVal;
}

export interface FuelAcMap {
  [ key: string ]: FuelAcVal;
}

export interface AcrissCodeRelatedFields {
  transmissionType: CarModelTransmissionType | '',
  fuelType: CarModelFuelType | '',
  driveType: CarModelDriveType | '',
  hasAirCo: boolean
}

export enum CarModelDriveType {
  AWD = 'AWD',
  _4WD = '_4WD',
  UNSPECIFIED = 'UNSPECIFIED'
}

export const carModelDriveTypeOptions = [
  { value: CarModelDriveType.AWD, label: 'AWD' },
  { value: CarModelDriveType._4WD, label: '4WD' },
  { value: CarModelDriveType.UNSPECIFIED, label: 'UNSPECIFIED' }
];

export enum CarModelTransmissionType {
  AUTOMATIC = 'AUTOMATIC',
  MANUAL = 'MANUAL',
  UNSPECIFIED = 'UNSPECIFIED'
}

export const carModelTransmissionTypeOptions = [
  { value: CarModelTransmissionType.AUTOMATIC, label: 'AUTOMATIC' },
  { value: CarModelTransmissionType.MANUAL, label: 'MANUAL' },
  { value: CarModelTransmissionType.UNSPECIFIED, label: 'UNSPECIFIED' }
];

export enum CarModelFuelType {
  DIESEL = 'DIESEL',
  ELECTRIC = 'ELECTRIC',
  ETHANOL = 'ETHANOL',
  HYBRID = 'HYBRID',
  HYDROGEN = 'HYDROGEN',
  LPG_COMPRESSED_GAS = 'LPG_COMPRESSED_GAS',
  MULTI_FUEL = 'MULTI_FUEL',
  PETROL = 'PETROL',
  UNSPECIFIED = 'UNSPECIFIED'
}

export const carModelFuelTypeOptions = [
  { value: CarModelFuelType.DIESEL, label: 'DIESEL' },
  { value: CarModelFuelType.ELECTRIC, label: 'ELECTRIC' },
  { value: CarModelFuelType.ETHANOL, label: 'ETHANOL' },
  { value: CarModelFuelType.HYBRID, label: 'HYBRID' },
  { value: CarModelFuelType.HYDROGEN, label: 'HYDROGEN' },
  { value: CarModelFuelType.LPG_COMPRESSED_GAS, label: 'LPG COMPRESSED GAS' },
  { value: CarModelFuelType.MULTI_FUEL, label: 'MULTI FUEL' },
  { value: CarModelFuelType.PETROL, label: 'PETROL' },
  { value: CarModelFuelType.UNSPECIFIED, label: 'UNSPECIFIED' }
];

export const carModelViewRoles = [ UserRole.SUPPLIER_MANAGE, UserRole.FLEET_MANAGE, UserRole.FLEET_VIEW ];
export const carModelEditRoles = [ UserRole.SUPPLIER_MANAGE, UserRole.FLEET_MANAGE ];

/*
* Desks
*/
export interface DeskRequest {
  code: string;
  groupId: number;
  deskType: DeskType;
  iataCode: string | null;
  name: string;
  safRate: number;
  deskLocationType: DeskLocationType;
  isWalkInSupported: boolean;
  isMeetGreetSupported: boolean;
  statusType: StatusType;
  pickupInstructions: string;
  returnInstructions: string;
  supplierDeskCode: string;
  address: AddressModel;
  contact: ContactModel;
  nameTranslations: TranslationModel[];
  pickupInstructionsTranslations: TranslationModel[];
  returnInstructionsTranslations: TranslationModel[];
  itemPrices: MandatoryItemPriceModel[];
}

export interface EditDeskRequest {
  groupId: number;
  deskType: DeskType;
  iataCode: string;
  name: string;
  safRate: number;
  deskLocationType: DeskLocationType;
  isWalkInSupported: boolean;
  isMeetGreetSupported: boolean;
  statusType: StatusType;
  pickupInstructions: string;
  returnInstructions: string;
  supplierDeskCode: string;
  address: AddressModel;
  contact: ContactModel;
  nameTranslations: TranslationModel[];
  pickupInstructionsTranslations: TranslationModel[];
  returnInstructionsTranslations: TranslationModel[];
  itemPrices: MandatoryItemPriceModel[];
}

export interface DeskModel {
  id: number;
  group: DeskGroupModel;
  code: string;
  deskType: DeskType;
  iataCode: string;
  name: string;
  safRate: number;
  deskLocationType: DeskLocationType;
  isWalkInSupported: boolean;
  isMeetGreetSupported: boolean;
  statusType: StatusType;
  pickupInstructions: string;
  returnInstructions: string;
  supplierDeskCode: string;
  address: AddressModel;
  contact: ContactModel;
  nameTranslations: TranslationModel[];
  pickupInstructionsTranslations: TranslationModel[];
  returnInstructionsTranslations: TranslationModel[];
  itemPrices: MandatoryItemPriceModel[];

  modifiedAt: string;
  modifiedBy: string;
  createdAt: string;
  createdBy: string;
}

export interface DeskGroupModel {
  id?: number;
  name: string;
  description: string;
  deskCount: number;

  modifiedAt?: string;
  modifiedBy?: string;
}

export interface DeskGroupRequest {
  name: string;
  description?: string;
}

export interface DeskGroupFilters {
  name?: string;
}

export interface AddressModel {
  city: string;
  country: string;
  address1: string;
  address2: string;
  state: string;
  timezone: string;
  postalCode: string;
  latitude: number;
  longitude: number;
}

export interface ContactModel {
  phone: string;
  emergencyPhone: string;
  email: string;
  fax: string;
}

export interface DeskListFilters {
  combinedFieldFilter?: string;
  groups?: string;
  status?: string;
  pageNumber?: number;
}

export interface ApplyOpeningsRequest {
  openingsOriginDeskCode: string;
}

export enum DeskType {
  TERMINAL = 'TERMINAL',
  OFF_SITE = 'OFF_SITE',
  ON_SITE = 'ON_SITE',
  MEET_GREET = 'MEET_GREET',
  OFFICE = 'OFFICE'
}

export const deskTypeOptions = [
  { value: DeskType.TERMINAL, label: 'TERMINAL' },
  { value: DeskType.OFF_SITE, label: 'OFF SITE' },
  { value: DeskType.ON_SITE, label: 'ON SITE' },
  { value: DeskType.MEET_GREET, label: 'MEET GREET' },
  { value: DeskType.OFFICE, label: 'OFFICE' }
];

export enum DeskLocationType {
  AIRPORT = 'AIRPORT',
  PORT = 'PORT',
  TRAINSTATION = 'TRAINSTATION',
  OTHER = 'OTHER'
}

export const deskLocationTypeOptions = [
  { value: DeskLocationType.AIRPORT, label: 'AIRPORT' },
  { value: DeskLocationType.PORT, label: 'PORT' },
  { value: DeskLocationType.TRAINSTATION, label: 'TRAINSTATION' },
  { value: DeskLocationType.OTHER, label: 'OTHER' }
];

export enum RateAdjustmentManagementType {
  MANUAL = 'MANUAL',
  AUTOMATIC = 'AUTOMATIC'
}

export const rateAdjustmentManagementTypeOptions = [
  { value: RateAdjustmentManagementType.AUTOMATIC, label: 'Automatic' },
  { value: RateAdjustmentManagementType.MANUAL, label: 'Manual' }
];

export const deskViewRoles = [ UserRole.SUPPLIER_MANAGE, UserRole.DESK_MANAGE, UserRole.DESK_VIEW ];
export const deskEditRoles = [ UserRole.SUPPLIER_MANAGE, UserRole.DESK_MANAGE ];

/*
* Products
*/
export interface ProductModel {
  id: number;
  name: string;
  code: string;
  supplierReferenceCode: string;
  fuelPolicyType: FuelPolicyType;
  mileagePolicyType: MileagePolicyType;
  mileageUnitType: MileageUnitType;
  mileageUnitPeriodType: MileageUnitPeriodType;
  mileageQuantity: number;
  pricingSumByDay: boolean;
  statusType: StatusType;
  description: string;
  items: ProductItemModel[];
  countries: ProductCountryModel[];
  excesses: ExcessModel[];
  rateAdjustmentManagementType: RateAdjustmentManagementType;
  rateAdjustmentCoefficient: number;

  modifiedAt: string;
  modifiedBy: string;
  createdAt: string;
  createdBy: string;

}

export interface ProductCountryModel {
  countryCode: string;
}

export interface ProductItemModel {
  id: number;
  name: string;
}

export interface ProductItemRequest {
  id: number;
}

export interface ProductRequest {
  name: string;
  code: string;
  supplierReferenceCode: string;
  fuelPolicyType: FuelPolicyType;
  mileagePolicyType: MileagePolicyType;
  mileageUnitType: MileageUnitType;
  mileageUnitPeriodType: MileageUnitPeriodType;
  mileageQuantity: number;
  pricingSumByDay: boolean;
  statusType: StatusType;
  description: string;
  items: ProductItemRequest[];
  countries: ProductCountryModel[];
  excesses: ExcessModel[];
  rateAdjustmentManagementType: RateAdjustmentManagementType;
  rateAdjustmentCoefficient: number;
}

export enum MileagePolicyType {
  LIMITED = 'LIMITED',
  UNLIMITED = 'UNLIMITED'
}

export const mileagePolicyTypeOptions = [
  { value: MileagePolicyType.LIMITED, label: 'LIMITED' },
  { value: MileagePolicyType.UNLIMITED, label: 'UNLIMITED' }
];

export enum MileageUnitType {
  KM = 'KM',
  MILE = 'MILE'
}

export const mileageUnitTypeOptions = [
  { value: MileageUnitType.KM, label: 'KM' },
  { value: MileageUnitType.MILE, label: 'MILE' }
];

export enum MileageUnitPeriodType {
  DAY = 'DAY',
  WEEK = 'WEEK',
  RENTAL_PERIOD = 'RENTAL_PERIOD',
  MONTH = 'MONTH'
}

export const mileageUnitPeriodTypeOptions = [
  { value: MileageUnitPeriodType.DAY, label: 'DAY' },
  { value: MileageUnitPeriodType.WEEK, label: 'WEEK' },
  { value: MileageUnitPeriodType.RENTAL_PERIOD, label: 'RENTAL PERIOD' },
  { value: MileageUnitPeriodType.MONTH, label: 'MONTH' }
];

export enum PricingStrategyType {
  RENTAL_DURATION = 'RENTAL_DURATION',
  RENTAL_DAYS = 'RENTAL_DAYS'
}

export const pricingStrategyOptions = [
  { value: PricingStrategyType.RENTAL_DURATION, label: 'Rate of pickup date  x  rental days' },
  { value: PricingStrategyType.RENTAL_DAYS, label: 'Sum of rates of each rental day' }
];

export enum FuelPolicyType {
  FULL_TO_EMPTY = 'FULL_TO_EMPTY',
  FULL_TO_FULL = 'FULL_TO_FULL',
  FULL_TO_EMPTY_INCLUDED = 'FULL_TO_EMPTY_INCLUDED',
  PARTIAL_TO_PARTIAL = 'PARTIAL_TO_PARTIAL',
  SUPPLIER_DEFINED = 'SUPPLIER_DEFINED',
  UNSPECIFIED = 'UNSPECIFIED'
}

export const fuelPolicyTypeOptions = [
  { value: FuelPolicyType.FULL_TO_EMPTY, label: 'FULL TO EMPTY' },
  { value: FuelPolicyType.FULL_TO_FULL, label: 'FULL TO FULL' },
  { value: FuelPolicyType.FULL_TO_EMPTY_INCLUDED, label: 'FULL TO EMPTY INCLUDED' },
  { value: FuelPolicyType.PARTIAL_TO_PARTIAL, label: 'PARTIAL TO PARTIAL' },
  { value: FuelPolicyType.SUPPLIER_DEFINED, label: 'SUPPLIER DEFINED' },
  { value: FuelPolicyType.UNSPECIFIED, label: 'UNSPECIFIED' }
];

export const productViewRoles = [ UserRole.RATE_MANAGE, UserRole.PRODUCT_VIEW ];
export const productEditRoles = [ UserRole.RATE_MANAGE ];

export interface ProductFilters {
  name?: string;
  pageNumber?: number;
}

export interface ExcessModel {
  acrissCodes: string[];
  excessCurrencyCode: string;
  excessAmount: number;
  depositAmount?: number;
}

/*
* BrokerRate
*/

export interface BrokerRateModel {
  id: number;
  brokerId: number;
  code: string;
  rateType: RateType;
  statusType: StatusType;
  paymentType: PaymentType;
  name: string;
  baseRateAdjustment: number;
  brand: string;
  vatRate: number;
  supplierReferenceCode: string;
  additionalData: AdditionalData;
  bookingEngineApiKey?: string;
  payLocallyMarkup: number;
  productId: number;
  productCode: string;
  markups: BrokerRateMarkupModel[];
  itemPrices: MandatoryItemPriceModel[];
  comments: CommentModel[];

  modifiedAt: string;
  modifiedBy: string;

  createdAt: string;
  createdBy: string;
}

export interface CommentModel {
  text: string;
  dateCreated: string;
  createdBy: string;
}

export interface BrokerRatesStatisticsModel {
  brokerRateCode: string;
  rates: number;
  reservations: number;
  latestReservationTimestamp: string;
}

export interface BrokerRateRequest {
  brokerId: number;
  code: string;
  rateType: RateType;
  statusType: StatusType;
  paymentType: PaymentType;
  name: string;
  baseRateAdjustment: number;
  brand: string;
  vatRate: number;
  supplierReferenceCode: string;
  additionalData: AdditionalData;
  payLocallyMarkup: number;
  productId: number;
  markups: BrokerRateMarkupModel[];
  itemPrices: MandatoryItemPriceModel[];
  apiKey?: boolean;
  comment: string;
}

export interface EditBrokerRateRequest {
  brokerId: number;
  rateType: RateType;
  statusType: StatusType;
  paymentType: PaymentType;
  name: string;
  baseRateAdjustment: number;
  brand: string;
  vatRate: number;
  supplierReferenceCode: string;
  additionalData: AdditionalData;
  payLocallyMarkup: number;
  productId: number;
  markups: BrokerRateMarkupModel[];
  itemPrices: MandatoryItemPriceModel[];
  apiKey?: boolean;
  comment: string;
}

export interface AdditionalData {
  emailTemplateBrand: string;
}

export interface BrokerRateMarkupModel {
  daysFrom: number;
  markupRate: number;
}

export enum PaymentType {
  NO_PREPAY = 'NO_PREPAY',
  PARTIAL_PREPAY = 'PARTIAL_PREPAY',
  FULL_PREPAY = 'FULL_PREPAY',
  ALL_PREPAY = 'ALL_PREPAY'
}

export const paymentTypeOptions = [
  { value: PaymentType.NO_PREPAY, label: 'NO PREPAY' },
  { value: PaymentType.PARTIAL_PREPAY, label: 'PARTIAL PREPAY' },
  { value: PaymentType.FULL_PREPAY, label: 'FULL PREPAY' },
  { value: PaymentType.ALL_PREPAY, label: 'ALL PREPAY' }
];

export enum RateType {
  RETAIL = 'RETAIL',
  NET = 'NET',
}

export const rateTypeOptions = [
  { value: RateType.RETAIL, label: 'RETAIL' },
  { value: RateType.NET, label: 'NET' }
];

export const brokerRateViewRoles = [ UserRole.BROKER_RATE_MANAGE, UserRole.SUPPLIER_MANAGE ];
export const brokerRateEditRoles = [ UserRole.BROKER_RATE_MANAGE, UserRole.SUPPLIER_MANAGE ];

export interface BrokerRateFilters {
  combinedFilterField?: string;
  brokerId?: number;
  productId?: number;
  itemId?: number;
  status?: StatusType;
  paymentType?: PaymentType;
  rateType?: RateType;
  page?: number;
}


/*
* Items
*/
export interface ItemModel {
  id: number;
  otaCode: string;
  itemGroupType: ItemGroupType;
  statusType: StatusType;
  maxQuantity: number;
  name: string;
  description: string;
  supplierReferenceCode: string;
  nameTranslations: TranslationModel[];
  descriptionTranslations: TranslationModel[];
  image: Image;
  modifiedAt: string;
  modifiedBy: string;
}

export interface ItemRequest {
  otaCode: string;
  statusType: StatusType;
  maxQuantity: number;
  name: string;
  base64Image: string;
  description: string;
  itemGroupType: ItemGroupType;
  supplierReferenceCode: string;
  nameTranslations: TranslationModel[];
  descriptionTranslations: TranslationModel[];
}

export enum ItemGroupType {
  EXTRA = 'EXTRA',
  COVERAGE = 'COVERAGE',
  FEE = 'FEE'
}

export const itemGroupTypeOptions = [
  { value: ItemGroupType.EXTRA, label: 'EXTRA' },
  { value: ItemGroupType.COVERAGE, label: 'COVERAGE' },
  { value: ItemGroupType.FEE, label: 'FEE' }
];

export const itemViewRoles = [ UserRole.RATE_CHAIN_ADMIN, UserRole.RATE_CHAIN_SUPPORT, UserRole.SUPPLIER_ADMIN ];
export const itemEditRoles = [ UserRole.RATE_CHAIN_ADMIN, UserRole.RATE_CHAIN_SUPPORT, UserRole.SUPPLIER_ADMIN ];

export interface ItemListFilters {
  combinedFilterField?: string;
  type?: ItemGroupType;
  pageNumber?: number;
}

/*
* Translation
*/

export interface TranslationModel {
  language: Locale;
  value?: string;
}

export enum Locale {
  DE_DE = 'DE_DE',
  RU_RU = 'RU_RU',
  IS_IS = 'IS_IS',
  RO_RO = 'RO_RO'
}

export const localeOptions = [
  { value: Locale.DE_DE, label: 'DE' },
  { value: Locale.RU_RU, label: 'RU' },
  { value: Locale.IS_IS, label: 'IS' },
  { value: Locale.RO_RO, label: 'RO' }
];

/*
* Supplier
*/

export interface SupplierInfoModel {
  supplierCode: string;
  baseCurrencyCode: string;
  maxRentalDuration: number;
  gracePeriodHours: number;
  minTimeToPickup?: number;
  maxDriverAge?: number;
  minTimeToCancel?: number;
  hasBookingWeb: boolean;
  modifiedBy?: string;
  modifiedAt?: string;
  tableauEmail: string;
  additionalData?: AdditionalDataSupplier;
  backendIntegration?: BackendIntegration;
  tableauInsightsUrl: string;
  tableauBillingUrl: string;
}

export interface AdditionalDataSupplier {
  reservationsEmailTo?: string[];
  reservationsEmailBcc?: string;
  customerEmailApiKey?: string;
  customerEmailFrom?: string;
  sendConfirmed?: boolean;
  sendPending?: boolean;
  sendCancellation?: boolean;
  sendBackendCancellationFailed?: boolean;
  sendBackendCreationFailed?: boolean;
  sendReservationAsAttachment?: boolean;
}

export interface BackendIntegration {
  backendIntegrationType?: BackendIntegrationType;
  username?: string;
  password?: string;
  apiKey?: string;
  url?: string;
  authenticationType?: BackendIntegrationAuthenticationType;

}

export enum BackendIntegrationAuthenticationType {
  BASIC_AUTH = 'BASIC_AUTH',
  API_KEY = 'API_KEY'
}

export const BackendIntegrationAuthenticationTypeOptions = [
  { value: BackendIntegrationAuthenticationType.BASIC_AUTH, label: 'Basic auth' },
  { value: BackendIntegrationAuthenticationType.API_KEY, label: 'Api key' }
];

export enum BackendIntegrationType {
  ANNATA = 'ANNATA',
  BLUEBIRD = 'BLUEBIRD',
  IVECH = 'IVECH',
  WEBHOOK = 'WEBHOOK'
}

export const BackendIntegrationTypeOptions = [
  { value: BackendIntegrationType.ANNATA, label: 'Annata' },
  { value: BackendIntegrationType.BLUEBIRD, label: 'Bluebird' },
  { value: BackendIntegrationType.WEBHOOK, label: 'Webhook' }
];

export interface SupplierRequest {
  maxRentalDuration: number;
  gracePeriodHours: number;
  minTimeToPickup?: number;
  maxDriverAge?: number;
  minTimeToCancel?: number;
  tableauEmail: string;
  additionalData?: AdditionalDataSupplier;
  tableauInsightsUrl?: string;
  tableauBillingUrl?: string;
  backendIntegration?: BackendIntegration;
}


/*
* Desk openings
*/

export interface DaysClosed {
  isMonClosed: boolean;
  isTueClosed: boolean;
  isWedClosed: boolean;
  isThuClosed: boolean;
  isFriClosed: boolean;
  isSatClosed: boolean;
  isSunClosed: boolean;
}

export interface DeskOpeningModel {
  id: number;
  deskId: number;
  validFrom: string;
  validTo: string;
  statusType: StatusType;
  pickupOutOfHoursType: OutOfHoursType;
  returnOutOfHoursType: OutOfHoursType;
  deskOpeningType: DeskOpeningType;
  daysClosed: DaysClosed;
  openHours: OpenHoursModel[];

  modifiedAt: string;
  modifiedBy: string;
}

export interface DeskOpeningRequest {
  validFrom: string;
  validTo: string;
  statusType: StatusType;
  pickupOutOfHoursType: OutOfHoursType;
  returnOutOfHoursType: OutOfHoursType;
  daysClosed?: DaysClosed;
  openHours?: OpenHoursModel[];
  deskOpeningType: DeskOpeningType;
}

export interface OpenHoursModel {
  startTime: number;
  endTime: number;
  dayOfWeek: string;
}

export enum OutOfHoursType {
  FREE_SELL = 'FREE_SELL',
  ON_REQUEST = 'ON_REQUEST',
  NOT_ALLOWED = 'NOT_ALLOWED'
}

export const OutOfHoursTypeOptions = [
  { value: OutOfHoursType.FREE_SELL, label: 'FREE SELL' },
  { value: OutOfHoursType.ON_REQUEST, label: 'ON REQUEST' },
  { value: OutOfHoursType.NOT_ALLOWED, label: 'NOT ALLOWED' }
];

export enum DeskOpeningType {
  DEFAULT = 'DEFAULT',
  STANDARD = 'STANDARD',
  HOLIDAY = 'HOLIDAY'
}

export enum DayOfWeek {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY'
}

export const daysOpenOptions = [
  { value: 'closed', label: 'Closed' },
  { value: 'open', label: 'Open' },
  { value: '24h', label: 'Open 24 h' }
];

/*
* Desk opening exceptions
*/

export interface DeskOpeningExceptionModel {
  id: number;
  deskId: number;
  date: string;
  statusType: StatusType;
  isClosed: boolean;
  pickupOutOfHoursType: OutOfHoursType;
  returnOutOfHoursType: OutOfHoursType;
  openHoursExceptions: OpenHoursExceptionModel[];

  modifiedAt: string;
  modifiedBy: string;
}

export interface DeskOpeningExceptionRequest {
  date: string;
  statusType: StatusType;
  isClosed: boolean;
  pickupOutOfHoursType: OutOfHoursType;
  returnOutOfHoursType: OutOfHoursType;
  openHoursExceptions: OpenHoursExceptionModel[];
}

export interface OpenHoursExceptionModel {
  startTime: string;
  endTime: string;
}

export interface TimelineOpening {
  id: number;
  validFrom: string;
  validTo?: string;
  statusType: StatusType;
  isException: boolean;
}

export const setupModuleViewRoles = [ UserRole.SUPPLIER_MANAGE, UserRole.RATES_MANAGER, UserRole.BROKER ];
export const supplierViewRoles = [ UserRole.SUPPLIER_ADMIN, UserRole.RATE_CHAIN_ADMIN, UserRole.RATE_CHAIN_SUPPORT ];
export const supplierFullSyncRoles = [ UserRole.RATE_CHAIN_ADMIN ];

export interface SupplierFullSyncStatus {
  supplier: string;
  startedAt: string | null;
  finishedAt: string | null;
  inProgress: boolean;
}

export interface SelectionItem {
  value: string;
  label: string;
}
